import { z } from 'zod/v4'
import {
  supportedLocales,
  userJobTypes,
  userTeamSizes,
  userTypesSettings,
  userUsages,
  validationRules,
} from '@skop-app/lib-domain/constants'
import type { Field } from '@skop-app/lib-domain/types'

import {
  countryField,
  isSwissCountry,
  phoneValidation,
  reactSelectValidation,
  siretValidation,
  stringValidation,
  vatValidation,
  zipcodeValidation,
} from '@/schemas/schemaValidations'
import { SESSION_INACTIVITY_OPTION_VALUES } from '@/services/auth/sessionPolicy'
import { type Language, type Translator } from '@/types'

export const getLoginFields = ({ t }: { t: Translator }) =>
  [
    {
      name: 'email',
      label: t('Signin.email'),
      type: 'email',
      className: 'col-span-12',
      autoComplete: 'email',
      spellCheck: false,
      validation: z.email({
        error: t('ZodErrorMessages.email-mandatory'),
      }),
    },
    {
      name: 'password',
      label: t('Signin.password'),
      type: 'password',
      className: 'col-span-12',
      autoComplete: 'current-password',
      validation: stringValidation(t)
        .min(1, {
          error: t('ZodErrorMessages.password-mandatory'),
        })
        .trim(),
    },
  ] as Field[]

export const getSignupFields = ({ t }) =>
  [
    {
      name: 'name',
      label: t('Signup.name'),
      placeholder: t('Signup.name-placeholder'),
      type: 'text',
      className: 'col-span-12',
      validation: stringValidation(t).regex(
        validationRules.name.pattern.value,
        {
          error: t('ZodErrorMessages.name'),
        },
      ),
    },
    {
      name: 'email',
      label: t('Signup.email'),
      placeholder: t('Signup.email-placeholder'),
      type: 'text',
      className: 'col-span-12',
      validation: z.email({
        error: t('ZodErrorMessages.email'),
      }),
    },
    {
      name: 'password',
      label: t('Signup.password'),
      type: 'password',
      className: 'col-span-12',
      validation: stringValidation(t).regex(
        validationRules.signinPassword.pattern.value,
        {
          error: t('ZodErrorMessages.password'),
        },
      ),
      showPasswordStrength: true,
    },
    {
      name: 'confirmPassword',
      label: t('Signup.confirm-password'),
      type: 'password',
      className: 'col-span-12',
      validation: z.string({
        error: t('Signup.confirm-password-validation'),
      }),
    },
    {
      name: 'conditions',
      label: (
        <span>
          {t('Signup.accept-conditions')}{' '}
          <a
            href="https://www.skop.app/conditions-generales-de-vente-et-dutilisation"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:underline"
          >
            {t('Signup.accept-conditions-link')}
          </a>
        </span>
      ),
      type: 'checkbox',
      className: 'col-span-12',
      validation: z.boolean({
        error: t('Signup.accept-conditions-error'),
      }),
    },
  ] as Field[]

export const getUpdatePasswordFields = ({ t }: { t: Translator }): Field[] => [
  {
    name: 'oldPassword',
    label: 'Saisissez votre mot de passe actuel',
    type: 'password',
    className: 'col-span-12',
    validation: stringValidation(t),
  },
  {
    name: 'newPassword',
    label: 'Saissisez votre nouveau mot de passe',
    type: 'password',
    className: 'col-span-12',
    validation: stringValidation(t).regex(
      validationRules.signinPassword.pattern.value,
      {
        error: t('ZodErrorMessages.password'),
      },
    ),
    showPasswordStrength: true,
  },
  {
    name: 'confirmPassword',
    label: 'Confirmez votre nouveau mot de passe',
    type: 'password',
    className: 'col-span-12',
    validation: z.string({
      error: t('Signup.confirm-password-validation'),
    }),
  },
]

export const getAccountInformationsFields = ({
  t,
  profilType,
  locale,
  country,
}: {
  t: Translator
  profilType: string
  locale: Language
  country?: string | null
}) => {
  const userTypes = userTypesSettings.filter((type) => type.value !== 'ADMIN')

  return [
    {
      name: 'id',
      placeholder: 'ID',
      type: 'text',
      className: 'hidden',
      disabled: true,
      validation: z.guid().optional(),
    },
    {
      label: t('Account.name'),
      name: 'name',
      placeholder: t('Account.name'),
      type: 'text',
      className: 'col-span-12 md:col-span-6',
      validation: stringValidation(t).regex(
        validationRules.name.pattern.value,
        {
          error: validationRules.name.pattern.message,
        },
      ),
      'aria-required': true,
    },
    {
      label: t('Account.email'),
      name: 'email',
      placeholder: t('Account.email'),
      type: 'text',
      className: 'col-span-12 md:col-span-6',
      validation: z.email({
        error: t('ZodErrorMessages.email'),
      }),
      disabled: true,
      'aria-required': true,
    },
    {
      label: t('Account.phone'),
      name: 'phone',
      placeholder: '07 81 80 86 72',
      type: 'text',
      className: 'col-span-12 md:col-span-6',
      validation: z.optional(phoneValidation(locale, t, true, country)),
    },
    {
      name: 'type',
      label: t('Account.type'),
      placeholder: t('Account.type'),
      type: 'select',
      options: userTypes.map((type) => ({
        label: t(`UserTypes.${type.value}`),
        value: type.value,
      })),
      validation: reactSelectValidation(t),
      className: 'col-span-12 md:col-span-6 md:col-start-1',
    },
    ...(profilType === 'NGO' || profilType === 'COMPANY'
      ? [
          {
            name: 'userTeamSize',
            label: t('UserSurvey.userTeamSize'),
            placeholder: t('UserSurvey.userTeamSizePlaceholder'),
            type: 'select',
            options: userTeamSizes?.map((size) => ({
              label: t(`UserSurvey.userTeamSizeDescription.${size?.value}`),
              value: size?.value,
            })),
            validation: z.optional(reactSelectValidation(t)),
            className: 'col-span-12 md:col-span-6',
          },
        ]
      : []),
    {
      name: 'userJob',
      label: t('UserSurvey.userJob'),
      placeholder: t('UserSurvey.userJobPlaceholder'),
      type: 'select',
      options: userJobTypes?.map((job) => ({
        ...job,
        label: t(`UserSurvey.userJobTypes.${job?.value}`),
        value: job?.value,
      })),
      validation: z
        .object({
          brevoId: z.number(),
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      className: 'col-span-12 md:col-span-6',
    },
    ...(profilType === 'NGO' || profilType === 'COMPANY'
      ? [
          {
            name: 'company',
            label: t('Account.company'),
            placeholder: t('Account.company'),
            type: 'text',
            className: 'col-span-12 md:col-span-6 md:col-start-1',
            validation: stringValidation(t)
              .min(2, {
                error: t('ZodErrorMessages.minChar', { min: 2 }),
              })
              .max(80, {
                error: t('ZodErrorMessages.maxChar', { max: 80 }),
              }),
          },
          ...(!isSwissCountry(country)
            ? [
                {
                  name: 'SIRET',
                  label: t('Account.siret'),
                  placeholder: '362 521 879 00034',
                  type: 'text',
                  className: 'col-span-12 md:col-span-6 md:col-start-1',
                  validation: z.optional(siretValidation(t)),
                },
              ]
            : []),
          {
            name: 'VAT',
            label: t('Account.vat'),
            placeholder: 'FR 36 362 521 879',
            type: 'text',
            className: 'col-span-12 md:col-span-6',
            validation: z.optional(vatValidation(t)),
          },
          {
            name: 'address',
            label: t('Account.address'),
            placeholder: '48 rue rené clair',
            type: 'text',
            className: 'col-span-12 md:col-span-6 md:col-start-1',
            validation: stringValidation(t).max(60, {
              error: t('ZodErrorMessages.maxChar', { max: 60 }),
            }),
          },
          countryField(t),
          {
            name: 'zipcode',
            label: t('Account.zipcode'),
            placeholder: '75018',
            type: 'text',
            className: 'col-span-12 md:col-span-6 md:col-start-1',
            validation: zipcodeValidation(t, country),
          },
          {
            name: 'city',
            label: t('Account.city'),
            placeholder: 'Paris',
            type: 'text',
            className: 'col-span-12 md:col-span-6',
            validation: stringValidation(t)
              .min(2, {
                error: t('ZodErrorMessages.minChar', { min: 2 }),
              })
              .max(50, {
                error: t('ZodErrorMessages.maxChar', { max: 50 }),
              }),
          },
        ]
      : []),
    {
      name: 'preferences',
      label: t('Account.notifications'),
      type: 'fields-array',
      className: 'col-span-12 sm:col-span-6 sm:col-start-1',
      validation: z.array(
        z.object({
          'notification-review-mention': z.optional(z.boolean()),
          'notification-team': z.optional(z.boolean()),
          'notification-availability-skop': z.optional(z.boolean()),
        }),
      ),
      hideAddBtn: true,
      fields: [
        {
          name: 'notification-team',
          label: t('Account.notification_team'),
          type: 'checkbox',
          className: 'col-span-7 col-start-1',
        },
        {
          name: 'notification-review-mention',
          label: t('Account.notification_review_mention'),
          type: 'checkbox',
          className: 'col-span-7 col-start-1',
        },
        {
          name: 'notification-availability-skop',
          label: t('Account.notification_availability'),
          description: t('Account.notification_availability_description'),
          type: 'checkbox',
          className: 'col-span-7 col-start-1',
        },
      ],
    },
    {
      name: 'language',
      label: t('Account.language'),
      type: 'select',
      options: supportedLocales,
      validation: reactSelectValidation(t),
      className: 'col-span-12 sm:col-span-6 sm:col-start-1',
    },
    {
      name: 'sessionInactivityDuration',
      label: t('Account.session_duration'),
      placeholder: t('Account.session_duration_placeholder'),
      type: 'select',
      options: SESSION_INACTIVITY_OPTION_VALUES.map((value) => ({
        label: t(`Account.session_duration_options.${value}`),
        value,
      })),
      validation: z
        .object({
          label: z.string(),
          value: z.enum(SESSION_INACTIVITY_OPTION_VALUES),
        })
        .optional(),
      className: 'col-span-12 sm:col-span-4 sm:col-start-1',
    },
  ] as Field[]
}

export const getForgotPasswordFields = ({ t }: { t: Translator }): Field[] => [
  {
    name: 'password',
    label: t('Signup.password-new'),
    type: 'password',
    className: 'col-span-12',
    validation: stringValidation(t).regex(
      validationRules.signinPassword.pattern.value,
      {
        error: t('ZodErrorMessages.password'),
      },
    ),
    showPasswordStrength: true,
  },
  {
    name: 'confirmPassword',
    label: t('Signup.confirm-password'),
    type: 'password',
    className: 'col-span-12',
    validation: z.string({
      error: t('Signup.confirm-password-validation'),
    }),
  },
]

export const getForgotPasswordEmailFields = ({
  t,
}: {
  t: Translator
}): Field[] => [
  {
    name: 'email',
    label: t('Signup.email-mandatory'),
    type: 'text',
    className: 'col-span-12',
    validation: z.email({
      error: t('Signup.email-validation'),
    }),
  },
]

export const getAccountConfirmationFields = ({
  email,
  t,
}: {
  email: string
  t: Translator
}): Field[] =>
  [
    ...(!email
      ? [
          {
            name: 'email',
            label: t('Account.confirm-email-validation'),
            placeholder: 'mon.nom@entreprise.fr',
            type: 'text',
            className: 'col-span-12 text-left',
            validation: z.email({
              error: t('ZodErrorMessages.email'),
            }),
          },
        ]
      : []),
  ] as Field[]

export const getUserSurveyFields = ({
  profilType,
  t,
}: {
  profilType: string
  t: Translator
}) =>
  [
    {
      name: 'userType',
      label: t('UserSurvey.youAre'),
      placeholder: t('UserSurvey.youArePlaceholder'),
      type: 'select',
      options: userTypesSettings?.map((type) => ({
        label: t(`UserTypes.${type?.value}`),
        value: type?.value,
      })),
      validation: reactSelectValidation(t),
      className: 'col-span-12',
    },
    ...(profilType === 'COMPANY' || profilType === 'NGO'
      ? [
          {
            name: 'userJob',
            label: t('UserSurvey.userJob'),
            placeholder: t('UserSurvey.userJobPlaceholder'),
            type: 'select',
            options: userJobTypes?.map((job) => ({
              ...job,
              label: t(`UserSurvey.userJobTypes.${job?.value}`),
              value: job?.value,
            })),
            validation: z.object({
              brevoId: z.number(),
              label: z.string(),
              value: z.string(),
            }),
            className: 'col-span-12',
          },
          {
            name: 'userTeamSize',
            label: t('UserSurvey.userTeamSize'),
            placeholder: t('UserSurvey.userTeamSizePlaceholder'),
            type: 'select',
            options: userTeamSizes?.map((size) => ({
              label: t(`UserSurvey.userTeamSizeDescription.${size?.value}`),
              value: size?.value,
            })),
            validation: reactSelectValidation(t),
            className: 'col-span-12',
          },
        ]
      : []),
    {
      name: 'userUsage',
      label: t('UserSurvey.userUsage'),
      placeholder: t('UserSurvey.userUsagePlaceholder'),
      type: 'select',
      options: userUsages?.map((usage) => ({
        label: t(`UserSurvey.userUsages.${usage?.value}`),
        value: usage?.value,
      })),
      isMulti: true,
      description: t('UserSurvey.multipleUsages'),
      validation: z.array(z.object({ label: z.string(), value: z.string() })),
      className: 'col-span-12',
    },
  ] as Field[]
